import React, { useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import EventService from "../../utils/EventService";
import { Dialog } from "../Dialog/Dialog";
import { Icon } from "../Icon/icon";
import * as CookieConsentConstants from "./CookieConsentConstants";
import { ResourceItem } from "../../commonTypes/resourceItem";
import { makeTranslator } from "../../utils/translations";
import { CheckBox, CheckBoxState } from "../FormControls/CheckBox";

let Translate: (key: string, defaultValue?: string) => string;
export interface ICookieConsentManagerProps {
  translations: ResourceItem[];
  Text: string;
  RequiredCookiesText: string;
  FunctionalCookiesText: string;
  StatisticsCookiesText: string;
  ReadMoreLink?: string;
  ReadMoreExternalLink: string;
  OnlyShowMangeCookieSettingsButton?: boolean;
  MangeCookieSettingsButtonText?: string;
  MangeCookieSettingsButtonHeading?: string;
}

export interface ICookieConsentManagerState extends GivenConsent {
  showCookieBanner: boolean;
}

export class GivenConsent {
  acceptFunctionality: boolean;
  acceptStatistics: boolean;
}

/**
 * Shows cookie options dialog and saves users preferences.
 *
 * Also fires an event when the user consents to functional or statistical cookies.
 * Use the following code to subscribe to the user approval event: EventService.subscribe(CookieConsentConstants.functionalConsentEvent or CookieConsentConstants.statisticsConsentEvent, callback);
 */
export const CookieConsentManager = (props: ICookieConsentManagerProps) => {
  Translate = makeTranslator(props.translations);

  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [acceptFunctionality, setAcceptFunctionality] = useState(false);
  const [acceptStatistics, setAcceptStatistics] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalHeading = useRef(null);
  const cookieAttributes = {
    expires: 365,
    sameSite: "strict",
    secure: true,
  } as Cookies.CookieAttributes;

  useEffect(
    () => {
      let functionalCookieConsent = Cookies.get(
        CookieConsentConstants.functionalCookieName
      );
      let statisticsCookieConsent = Cookies.get(
        CookieConsentConstants.statisticsCookieName
      );
      const hasGivenConsent =
        functionalCookieConsent !== undefined &&
        statisticsCookieConsent !== undefined;

      if (hasGivenConsent) {
        if (functionalCookieConsent === "1") {
          EventService.fire(CookieConsentConstants.functionalConsentEvent, {});
          setAcceptFunctionality(true);
        }
        if (statisticsCookieConsent === "1") {
          EventService.fire(CookieConsentConstants.statisticsConsentEvent, {});
          setAcceptStatistics(true);
        }
      } else {
        setShowCookieBanner(true);
      }
    },
    [] /*only run when the component first renders.*/
  );

  const consentGiven = (
    cosentsToFunctionality: boolean,
    cosentsToStatistics: boolean
  ) => {
    removeAllCookies();

    if (cosentsToFunctionality) {
      EventService.fire(CookieConsentConstants.functionalConsentEvent, {});
    }
    if (cosentsToStatistics) {
      EventService.fire(CookieConsentConstants.statisticsConsentEvent, {});
    } else {
      EventService.fire(CookieConsentConstants.statisticsConsentDenyEvent, {});
    }

    Cookies.set(
      CookieConsentConstants.functionalCookieName,
      cosentsToFunctionality ? "1" : "0",
      cookieAttributes
    );
    Cookies.set(
      CookieConsentConstants.statisticsCookieName,
      cosentsToStatistics ? "1" : "0",
      cookieAttributes
    );

    setShowCookieBanner(false);
  };
  const removeAllCookies = () => {
    const hostParts = window.location.host.split(".");
    const domains = hostParts.reduce(
      (acc: string[], current, index) => [
        ...acc,
        hostParts.slice(index).join("."),
      ],
      []
    );

    Object.keys(Cookies.get()).forEach(function (cookieName) {
      domains.forEach((domain) =>
        Cookies.remove(cookieName, { path: "/", domain: domain })
      );
      Cookies.remove(cookieName, { path: "" });
    });
  };
  const saveCustomSettingsClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    consentGiven(acceptFunctionality, acceptStatistics);
    setIsModalOpen(false);
  };

  const acceptAllClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setAcceptFunctionality(true);
    setAcceptStatistics(true);

    consentGiven(true, true);

    if (isModalOpen) setIsModalOpen(false);
  };

  const acceptRequiredOnlyClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setAcceptFunctionality(false);
    setAcceptStatistics(false);

    consentGiven(false, false);

    if (isModalOpen) setIsModalOpen(false);
  };

  const openModalClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsModalOpen(true);
  };
  const OnafterModalOpen = () => {
    modalHeading.current.focus();
  };
  const closeModalClick = () => {
    setIsModalOpen(false);
  };

  const {
    Text,
    ReadMoreLink,
    ReadMoreExternalLink,
    OnlyShowMangeCookieSettingsButton,
    MangeCookieSettingsButtonHeading,
    MangeCookieSettingsButtonText,
  } = props;
  return (
    <div data-nosnippet>
      {OnlyShowMangeCookieSettingsButton && (
        <div className="container block-margin">
          <div className="row">
            <div className="col-lg-12 ">
              {MangeCookieSettingsButtonHeading && (
                <h2 className="sos-ck-cnsnt-mgnr__changeCokkieSetting__heading">
                  {MangeCookieSettingsButtonHeading}
                </h2>
              )}
              <button
                className="button sos-ck-cnsnt-mgnr__changeCokkieSetting__button"
                onClick={openModalClick}
              >
                {MangeCookieSettingsButtonText
                  ? MangeCookieSettingsButtonText
                  : Translate("manageCookies")}
              </button>
            </div>
          </div>
        </div>
      )}
      {showCookieBanner && !OnlyShowMangeCookieSettingsButton && (
        <>
          <div className="sos-ck-cnsnt-mgnr__overlay" />
          <section
            className="sos-ck-cnsnt-mgnr sos-ck-cnsnt-mgnr--bottom"
            aria-label={Translate("sectionAriaLabel")}
          >
            <div className="container">
              <div className="row">
                <div className="col-lg-12 sos-ck-cnsnt-mgnr__container ">
                  <div className="sos-ck-cnsnt-mgnr__content">
                    <p>
                      {Text}{" "}
                      {(ReadMoreExternalLink != null ||
                        ReadMoreLink != null) && (
                        <a href={ReadMoreExternalLink ?? ReadMoreLink}>
                          {Translate("readMoreText")}
                        </a>
                      )}
                    </p>
                  </div>
                  <div className="sos-ck-cnsnt-mgnr__buttons">
                    <button
                      className="button sos-ck-cnsnt-button"
                      onClick={acceptAllClick}
                    >
                      {Translate("acceptAll")}
                    </button>
                    <button
                      className="button sos-ck-cnsnt-button"
                      onClick={acceptRequiredOnlyClick}
                    >
                      {Translate("acceptRequiredOnly")}
                    </button>
                    <button
                      className="button button--white"
                      onClick={openModalClick}
                    >
                      {Translate("manageCookies")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
      <Dialog
        isOpen={isModalOpen}
        onRequestClose={closeModalClick}
        className="sos-ck-cnsnt-mgnr-modal ReactModal__Content--top"
        labelledby="cookie-modal-heading"
        onAfterOpen={OnafterModalOpen}
      >
        <button onClick={closeModalClick} className="close">
          <Icon
            isAriaHidden={false}
            ariaLabel="Stäng"
            iconType="ic_close_white_24px"
          />
        </button>
        <h3
          id="cookie-modal-heading"
          className="sos-ck-cnsnt-mgnr-modal__heading"
          ref={modalHeading}
          tabIndex={-1}
        >
          {Translate("manageCookies")}
        </h3>
        <p>
          {Translate("cookiesIntro")}{" "}
          {(ReadMoreExternalLink != null || ReadMoreLink != null) && (
            <a href={ReadMoreExternalLink ?? ReadMoreLink}>
              {Translate("readMoreText")}
            </a>
          )}
        </p>
        <div className="sos-ck-cnsnt-mgnr-modal__buttons">
          <button
            className="button sos-ck-cnsnt-button"
            onClick={acceptAllClick}
          >
            {Translate("acceptAll")}
          </button>
          <button
            className="button sos-ck-cnsnt-button"
            onClick={acceptRequiredOnlyClick}
          >
            {Translate("acceptRequiredOnly")}
          </button>
        </div>
        <p>{props.RequiredCookiesText}</p>
        <hr />
        <CheckBox
          label={Translate("settingsOption")}
          onChange={(e: CheckBoxState) => setAcceptFunctionality(e.value)}
          fieldName={Translate("settingsOption")}
          ariaDescribedBy="functionalCookiesText"
          value={acceptFunctionality}
        />
        <p id="functionalCookiesText">{props.FunctionalCookiesText}</p>
        <hr />
        <CheckBox
          label={Translate("statisticsOption")}
          onChange={(e: CheckBoxState) => setAcceptStatistics(e.value)}
          fieldName={Translate("statisticsOption")}
          ariaDescribedBy="statisticsCookiesText"
          value={acceptStatistics}
        />
        <p id="statisticsCookiesText">{props.StatisticsCookiesText}</p>
        <button
          className="button sos-ck-cnsnt-button sos-ck-cnsnt-mgnr-modal__save"
          onClick={saveCustomSettingsClick}
        >
          {Translate("save")}
        </button>
      </Dialog>
    </div>
  );
};

CookieConsentManager.defaultProps = {
  Text: "Vi använder kakor (cookies) för att ge dig en bättre upplevelse av vår webbplats. Genom att surfa vidare godkänner du att vi använder kakor. ",
  ReadMoreLink: "",
};
